import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Tooltip } from '@fluentui/react-components';
import { FluentButton } from '../../../../components/FluentUI/Fluent-Button';
import FluentUiIcon from '../../../../assets/fluent-ui-icon';
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import {
    clearDetailStateFromStorage, getAccordionMemberRowRender, getFilteredSortedData, getInitialDetailState, OverallCapacityCell, saveDetailStateToStorage, useMemberGroupToggle, getMembersTimeLogColumns,
    getDealTrackerModalColumns,
    handleStafferTimeLogExport,
    getDealsData,
    LevfinNoMembersEmptyState,
    LevfinBankerNoDealOnFilterState
} from '../levfin-helper';
import { FluentAvatar } from '../../../../components/FluentUI/Fluent-Avatar';
import {
    createColumnMenu,
    formatLastUpdatedDate,
    renderTooltipHeader,
    submitButton,
    getLastUpdateTooltip,
    renderlastUpdatedTooltipHeader,
} from "../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import { isFilteredColumn } from "../../dashboard-util/dashboard-telerik-helper/widget-grid-filter";
import { getLastUpdatedDateColorWeeks } from '../../dashboard-util/dashboard-utils';
import "../levfin-junior-banker/levfin-time-log.scss";
import { useFluentToast } from '../../../../components/FluentUI/Fluent-Toast';
import { LoginContext } from '../../../../services/api/authentication-api';
import { useHistory } from "react-router-dom";
import LevfinMembersTimeLogModal from './levfin-members-time-log-modal';
import { SaveLevfinBankerDataOnStafferDashboard } from '../../../../services/api/dashboard-api';
import { ExcelExport } from "@progress/kendo-react-excel-export";

const LevfinMembersTimeLog = ({
    members,
    fetchLevfinStafferTimeLog,
    setIsLevfinStafferTimeLogLoading
}) => {
    const user = useContext(LoginContext);
    const history = useHistory();
    const [data, setData] = useState(members);
    const [detailState, setDetailState] = useState(() => getInitialDetailState(user?.userID, "levfinMemberStafferTimeLog"));
    const { sort, filter } = detailState;
    const [isSaving, setIsSaving] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [expandClass, setExpandClass] = useState("");
    // const flattenedData = useMemo(() => getFlattenedData(data), [data]);
    const flattenedData = useMemo(() => {
        const baseData = data;
        // Flatten NumberOfDeals values onto each data item so column menu filters can read them
        return baseData.map(item => {
            const flattened = { ...item };
            if (item?.NumberOfDeals && Array.isArray(item?.NumberOfDeals)) {
                item?.NumberOfDeals.forEach(deal => {
                    flattened[deal.Key] = deal.Value;
                });
            }
            return flattened;
        });
    }, [data]);
    const filteredData = useMemo(() => getFilteredSortedData(flattenedData, filter, sort), [flattenedData, filter, sort]);
    const visibleData = useMemo(() => filteredData, [filteredData]);
    const columnMenu = useMemo(() => createColumnMenu(flattenedData), [flattenedData]);
    const columnIsFiltered = useCallback(
        (fieldName) => isFilteredColumn(fieldName, filter),
        [filter]
    );
    const { groupState, toggleGroup, setGroupState } = useMemberGroupToggle();
    const AccordionRowRender = useMemo(() => getAccordionMemberRowRender(filteredData, groupState, toggleGroup, 8, "brand"), [filteredData, groupState, filter]);
    const { showToast, FluentToast } = useFluentToast();
    const canCreate = user?.currentUser?.privileges?.["Projects"]?.Add ?? false;
    const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
    const isEmpty = visibleData.length === 0;
    const [selectedMember, setSelectedMember] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const finalGridData = isEmpty
        ? [{
            isEmptyRow: true,
            isFilterEmptyState: (filter?.filters?.length ?? 0) > 0,
        }]
        : visibleData;

    const onOverallCapacityChange = useCallback((memberId, newCapacity) => {
        setData((prevData) =>
            prevData.map((item) =>
                item.BankerId === memberId ? { ...item, OverallCapacity: newCapacity, isChanged: true } : item
            )
        );
        setSubmitDisabled(false);
    }, []);
    const _mainExport = useRef(null);
    const _modalExport = useRef(null);

    const handleSubmit = async () => {
        setSubmitDisabled(true);
        setIsSaving(true);
        var dataToSave = data.filter(d => d.isChanged);
        var payload = dataToSave.map(d => ({
            UserId: d.BankerId,
            OverallCapacity: d.OverallCapacity,
            TaskId: d.OverallCapacityTaskId
        }));
        let result = await SaveLevfinBankerDataOnStafferDashboard(payload);
        if (result?.status === 200) {
            {
                if (result.data) {
                    showToast("Success", "Overall Capacity updated successfully", "success");
                    setData(prevData => prevData.map(d => ({ ...d, isChanged: false })));
                } else {
                    showToast("Error", "Unexpected response from server", "error");
                }
            }
        }
        else {
            showToast("Error", "Failed to update Overall Capacity", "error");
        }
        setIsSaving(false);
    };


    const handleDetailStateChange = (newState) => {
        const userId = user?.userID;
        setDetailState(newState);
        saveDetailStateToStorage(userId, newState, "", "levfinMemberStafferTimeLog");
    };

    const hasAnyFilter =
        detailState.filter &&
        Array.isArray(detailState.filter.filters) &&
        detailState.filter.filters.length > 0;

    const canReset = detailState.sort.length > 0 || hasAnyFilter;
    const isSubmitDisabled = isSaving || submitDisabled;
    const tooltipText = getLastUpdateTooltip();
    const submitBtn = submitButton(isSubmitDisabled, handleSubmit);

    const resetDataState = () => {
        const resetState = { sort: [], filter: null };
        setDetailState(resetState);
        clearDetailStateFromStorage(user?.userID, "levfinMemberStafferTimeLog");
    };
    const onRowClick = (event) => {
        setSelectedMember(event.dataItem);
        setShowModal(true);
    };

    const onHide = () => {
        setShowModal(false);
        setSelectedMember(null);
    }

    return (
        <>
            <div className={`levfin-time-log ${expandClass} dashboard-container-shadow`}>
                <div className="widget-header">
                    <div className="header-left">
                        <div className="main-title-badge-row">
                            <div className="main-title">Analysts & Associates/VPs Log</div>
                        </div>
                        <div className="sub-title">GIB/Leveraged Finance</div>
                    </div>
                    <div className="header-right">
                        <Tooltip content="Reset Filters and Sorting">
                            <FluentButton
                                appearance="secondaryWithOutline"
                                icon={
                                    <FluentUiIcon
                                        name="ArrowResetRegular"
                                        color={canReset ? "#0F6CBD" : "#A0A0A0"}
                                        size={20}
                                        disabled={!canReset}
                                    />
                                }
                                disabled={!canReset}
                                onClick={resetDataState}
                            />
                        </Tooltip>

                        <Tooltip content="Export Data">
                            <FluentButton
                                appearance="secondaryWithOutline"
                                icon={
                                    <FluentUiIcon
                                        name="ArrowDownloadRegular"
                                        color="#0F6CBD"
                                        size={20}
                                    />
                                }
                                onClick={() =>
                                    handleStafferTimeLogExport({ mainRef: _mainExport, modalRef: _modalExport, mainData: flattenedData, categoryOptions: members[0]?.CategoryOptions ?? [] })
                                }
                            />
                        </Tooltip>
                        <Tooltip content="Expand Widget">
                            <span>
                                <FluentButton
                                    appearance="secondaryWithOutline"
                                    icon={
                                        <FluentUiIcon
                                            name={
                                                expandClass === "widgetExpanded"
                                                    ? "FullScreenMinimizeFilled"
                                                    : "FullScreenMaximizeFilled"
                                            }
                                            color={"#0F6CBD"}
                                            size={20}
                                        />
                                    }
                                    onClick={() => setExpandClass(expandClass === "" ? "widgetExpanded" : "")}
                                />
                            </span>
                        </Tooltip>
                        <FluentButton
                            text="Create Deal"
                            appearance="secondaryWithOutline"
                            disabled={!canCreate}
                            icon={
                                <FluentUiIcon
                                    name="FolderAddRegular"
                                    color={canCreate ? "#0F6CBD" : "#A0A0A0"}
                                    size={20}
                                />
                            }
                            onClick={() => {
                                if (canCreate) {
                                    history.push({
                                        pathname: "/projects/create",
                                        state: { fromDashboard: true },
                                    });
                                }
                            }}
                        />

                        {tooltipText ? (
                            <Tooltip content={tooltipText} relationship="label" positioning="below">
                                <div>{submitBtn}</div>
                            </Tooltip>
                        ) : (
                            submitBtn
                        )}
                    </div>
                </div>
                <div className='detail-grid-scroll-container clickable-row'>
                    {finalGridData[0]?.isFilterEmptyState ? (
                        <LevfinBankerNoDealOnFilterState resetDataState={resetDataState} />
                    ) : !finalGridData || finalGridData.length === 0 || (finalGridData[0]?.isEmptyRow) ? (
                        <LevfinNoMembersEmptyState />
                    ) : (
                        <Grid
                            data={finalGridData}
                            dataItemKey={"Banker"}
                            sortable={true}
                            className=''
                            sort={detailState.sort}
                            filter={detailState.filter}
                            onSortChange={(e) => handleDetailStateChange({ ...detailState, sort: e.sort })}
                            onFilterChange={(e) =>
                                handleDetailStateChange({ ...detailState, filter: e.filter })
                            }
                            rowRender={AccordionRowRender}
                            resizable={true}
                            onRowClick={onRowClick}
                        >
                            <Column
                                field="Banker"
                                title={renderTooltipHeader("Banker")}
                                width={150}
                                columnMenu={columnMenu}
                                headerClassName={columnIsFiltered("Banker") ? "filtered" : ""}
                                cell={(props) => (
                                    <td className="summary-cell k-table-td avatar-cell">
                                        <div className="avatar-content">
                                            <FluentAvatar name={props.dataItem.Banker} color="brand" size={24} />
                                            <span>{props.dataItem.Banker}</span>
                                        </div>
                                    </td>

                                )}
                            />
                            <Column
                                field="OverallCapacity"
                                title={renderTooltipHeader("Overall Capacity")}
                                columnMenu={columnMenu}
                                cell={(props) => (
                                    <td className="summary-cell k-table-td overall-capacity">
                                        <OverallCapacityCell
                                            value={props.dataItem.OverallCapacity}
                                            taskExists={props.dataItem.OverallCapacityTaskId != null}
                                            onChange={(newCapacity) => onOverallCapacityChange(props.dataItem.BankerId, newCapacity)}
                                            isBanker={false}
                                            canEdit={props.dataItem.CanEdit}
                                        />
                                    </td>
                                )}
                            />
                            {members[0]?.CategoryOptions?.map((item) => (
                                <Column
                                    key={item}
                                    field={`${item}`}
                                    title={renderTooltipHeader(`# of ${item}`)}
                                    columnMenu={columnMenu}
                                    cell={(props) => (
                                        <td className="summary-cell k-table-td">
                                            {props.dataItem[item] ?? "—"}
                                        </td>
                                    )}
                                />
                            ))}

                            <Column
                                field="Total"
                                title={renderTooltipHeader("Total")}
                                columnMenu={columnMenu}
                                cell={(props) => (
                                    <td className="summary-cell k-table-td">
                                        {props.dataItem.Total ?? "—"}
                                    </td>
                                )}
                            />
                            <Column
                                field="LastUpdated"
                                title={renderlastUpdatedTooltipHeader("Last Updated")}
                                columnMenu={columnMenu}
                                filter="date"
                                cell={(props) => (
                                    <td className="background-white k-table-td" style={{ color: getLastUpdatedDateColorWeeks(props.dataItem.LastUpdated) }}>
                                        <span>{formatLastUpdatedDate(props.dataItem.LastUpdated)}</span>
                                    </td>
                                )}
                            />

                        </Grid>)}
                </div>
                {showModal && (
                    <LevfinMembersTimeLogModal
                        show={showModal}
                        onHide={onHide}
                        banker={selectedMember}
                        fetchLevfinStafferTimeLog={fetchLevfinStafferTimeLog}
                        setIsLevfinStafferTimeLogLoading={setIsLevfinStafferTimeLogLoading}
                    />
                )}
                {FluentToast}
            </div>
            <ExcelExport ref={_mainExport} fileName={`MembersTimeLog_Detail`} data={flattenedData}>
                {getMembersTimeLogColumns(members[0]?.CategoryOptions ?? [])}
            </ExcelExport>
            <ExcelExport data={getDealsData(flattenedData)} ref={_modalExport}>
                {getDealTrackerModalColumns()}
            </ExcelExport>
        </>
    );
};

export default LevfinMembersTimeLog;
